import React, { useEffect, useRef, useState } from 'react';
import { InputAdornment, TextField, TextFieldProps, Typography } from '@mui/material';

import { useFormContext } from './Form.Context';
import { camelCaseToUserText } from '@dw/utils';

export type TFormTextInputType = 'text' | 'number' | 'email' | 'tel' | 'password' | 'color' | 'url';

interface IFormTextInputProps<T> extends Omit<TextFieldProps, 'label' | 'type' | 'name' | 'disabled' | 'value' | 'onChange' | 'InputProps' | 'error' | 'onPaste'> {
    name: keyof T;
    label?: string;
    type?: TFormTextInputType;
    disabled?: boolean;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    maxLength?: number;
    /**
     * The on-screen keyboard to ask for. Use `numeric` for digit-only values that are not numbers to
     * do arithmetic on (a one-time code, a PIN) — `type='number'` would strip their leading zeros.
     */
    inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode'];
    min?: number;
    max?: number;
    /**
     * Granularity for `type='number'`. Defaults to `'any'`, which accepts decimals — pass `1` for a
     * value the API types as an integer, or the browser reports a typed `3.5` as valid and the save
     * fails on deserialization with a message that names nothing.
     */
    step?: number | 'any';
    /**
     * A regex SOURCE (no delimiters, no flags) the value must match, e.g. `'#[0-9a-fA-F]{6}'`.
     *
     * This is what puts a text field into the browser's `:invalid` set, which is what `Form` scans
     * on submit — a pattern held only in JavaScript and used to style something is not a gate, and
     * lets the client accept values the API will reject.
     */
    pattern?: string;
    helperText?: string;
    doNotShowRequiredAsterisk?: boolean;
    doNotShrinkLabel?: boolean;
    labelShrinked?: boolean;
    textCenter?: boolean;
    upperCase?: boolean;
    debounceTime?: number;
    onPaste?: (e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}
export function FormTextInput<T>(props: IFormTextInputProps<T>) {
    const {
        label,
        type,
        name,
        disabled,
        startIcon,
        endIcon,
        maxLength,
        inputMode,
        min,
        max,
        step,
        pattern,
        helperText,
        doNotShowRequiredAsterisk,
        doNotShrinkLabel,
        labelShrinked,
        textCenter,
        upperCase,
        debounceTime,
        onPaste,
        ...baseInputProps
    } = props;

    const {
        formData,
        handleFormChange,
        disabled: formDisabled,
        formErrors,
        customFormErrors,
        submitAttempted
    } = useFormContext<T>();

    const [hasFocus, setHasFocus] = useState(false);
    const [bufferValue, setBufferValue] = useState(formData[name]);

    const debounceTimeoutRef = useRef<NodeJS.Timeout>(null);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setBufferValue(newValue as any);

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        const processChange = () => {
            if (type === 'number') {
                let parsedValue : number | null = parseFloat(newValue);
                if (isNaN(parsedValue))
                    parsedValue = null;
    
                handleFormChange(name, parsedValue);
                return;
            }
            
            handleFormChange(name, newValue);
        }

        if (debounceTime) {
            debounceTimeoutRef.current = setTimeout(() => {
                processChange();
            }, debounceTime);
        } else {
            processChange();
        }
    };

    const handleColorInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const updatedValue = e.target.value as any;
        debounceTimeoutRef.current = setTimeout(() => {
            debounceTimeoutRef.current = null;
            setBufferValue(updatedValue);
            handleFormChange(name, updatedValue);
        }, 50);
    }

    useEffect(() => {
        if (formData[name] !== bufferValue) {
            setBufferValue(formData[name]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData[name]]);

    return (
        <TextField
            {...baseInputProps}
            label={label ?? camelCaseToUserText(String(name))}
            name={String(name)}
            type={type ?? 'text'}
            value={bufferValue}
            onChange={type === 'color' ? handleColorInputChange : handleInputChange}
            autoCapitalize={!type || type === 'text' ? 'on' : undefined}
            slotProps={{
                inputLabel: {
                    shrink: (!doNotShrinkLabel && ((!!bufferValue || bufferValue === '0' || bufferValue === 0) || hasFocus || !!startIcon)) || Boolean(labelShrinked),
                    required: doNotShowRequiredAsterisk ? false : undefined,
                    disabled: true
                },
                input: {
                    startAdornment: !startIcon ? null : (
                        <InputAdornment position='start'>
                            {startIcon}
                        </InputAdornment>
                    ),
                    endAdornment: !endIcon ? null : (
                        <InputAdornment position='end'>
                            {endIcon}
                        </InputAdornment>
                    ),
                    inputProps: {
                        min: min,
                        max: max,
                        maxLength: maxLength,
                        inputMode: inputMode,
                        onPaste: onPaste,
                        step: step ?? 'any',
                        pattern: pattern,
                        style: {
                            textAlign: textCenter ? 'center' : 'left',
                            textTransform: upperCase ? 'uppercase' : undefined
                        }
                    }
                }
            }}
            onFocus={() => setHasFocus(true)}
            onBlur={(e) => {
                setHasFocus(false);
                if (baseInputProps.onBlur) {
                    baseInputProps.onBlur(e);
                }
            }}
            disabled={disabled || formDisabled}
            error={submitAttempted && (formErrors[name] || !!customFormErrors[name])}
            helperText={submitAttempted && !!customFormErrors[name] ? (
                <Typography variant='caption' color='error'>{customFormErrors[name]}</Typography>
            ) : helperText}
        />
    )
};